import React, { useState } from "react";
import MainLayout from "../../layout/mainlayout.tsx";
import ContentHeader from "../../components/ContentHeader/ContentHeader.tsx";
import "./CreateTicket.css";
import { useAppSelector } from "../../app/hooks.ts";
import { 
  useGetCategoryTypesByCompanyQuery, 
  useGetCategoriesByCategoryTypeQuery,
  useGetSubcategoriesByCategoryQuery 
} from "../../services/category.api.ts";
import { selectAuth } from "../../features/authSlice.ts";
import { useGetAllTicketTypesQuery } from "../../services/ticket_type.api.ts";
import { useGetClientsByCompanyQuery } from "../../services/client.api.ts";
import { useAssignTicketMutation, useCreateTicketMutation } from "../../services/ticket.api.ts";
import { toast } from "react-toastify";
import { useGetAgentsByCompanyQuery } from "../../services/agent.api.ts";
import { useNavigate } from 'react-router-dom';

const CreateTicket: React.FC = () => {
  // logged in Agent Data
  const { agentData } = useAppSelector(selectAuth);
  const navigate = useNavigate();

  // Create Ticket Mutation
  const [createTicket, { isLoading: isCreatingTicket }] = useCreateTicketMutation();

  // Form state for independent attributes
  const [ticketTitle, setTicketTitle] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [ticketTypeId, setTicketTypeId] = useState<number | undefined>(undefined);
  const [clientId, setClientId] = useState<number | undefined>(undefined);

  // Form state for dependent attributes
  const [selectedCategoryTypeId, setSelectedCategoryTypeId] = useState<number | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<number | undefined>(undefined);

  // Fetch queries to fill form
  const { data: ticketTypes, isLoading: isTicketTypesLoading, isError: isTicketTypesError } = useGetAllTicketTypesQuery();
  const { data: clients, isLoading: isClientsLoading, isError: isClientsError } = useGetClientsByCompanyQuery(agentData!.belongs_to_company_id);
  const { data: categoryTypes, isLoading: isCategoryTypesLoading, isError: isCategoryTypesError } = useGetCategoryTypesByCompanyQuery(agentData!.belongs_to_company_id);
  const { data: categories, isLoading: isCategoriesLoading, isError: isCategoriesError } = useGetCategoriesByCategoryTypeQuery(
    selectedCategoryTypeId!, 
    { skip: !selectedCategoryTypeId }
  );
  const { data: subcategories, isLoading: isSubcategoriesLoading, isError: isSubcategoriesError } = useGetSubcategoriesByCategoryQuery(
    selectedCategoryId!, 
    { skip: !selectedCategoryId }
  );

  // Form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!ticketTitle || !ticketDescription || !ticketTypeId || !selectedCategoryTypeId || !selectedCategoryId || !clientId) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const ticketData = {
        ticket_title: ticketTitle,
        ticket_description: ticketDescription,
        ticket_type_id: ticketTypeId,
        category_type_id: selectedCategoryTypeId,
        category_id: selectedCategoryId,
        subcategory_id: selectedSubcategoryId,
        client_id: clientId,
        company_id: agentData!.belongs_to_company_id
      };

    const { ticket } = await createTicket(ticketData).unwrap();
    
    toast.success("Ticket created successfully");
    
    setCreatedTicketId(ticket.ticket_id);
    setShowAssignPopup(true);
      
      setTicketTitle("");
      setTicketDescription("");
      setTicketTypeId(undefined);
      setClientId(undefined);
      setSelectedCategoryTypeId(undefined);
      setSelectedCategoryId(undefined);
      setSelectedSubcategoryId(undefined);
    } catch (error) {
      console.error("Failed to create ticket", error);
      toast.error("Failed to create ticket. Please try again.");
    }
  };

  // Category type selection to reset category and subcategory after change
  const handleCategoryTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value ? parseInt(event.target.value) : undefined;
    setSelectedCategoryTypeId(value);
    setSelectedCategoryId(undefined);
    setSelectedSubcategoryId(undefined);
  };

  // Category selection to reset subcategory after change
  const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value ? parseInt(event.target.value) : undefined;
    setSelectedCategoryId(value);
    setSelectedSubcategoryId(undefined);
  };

  // Add these new state variables for popup
  const [showAssignPopup, setShowAssignPopup] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState<number | undefined>(undefined);
  const [selectedAgentId, setSelectedAgentId] = useState<number | undefined>(undefined);

  // Fetch agents query
  const { data: agents, isLoading: isAgentsLoading, isError: isAgentsError } = useGetAgentsByCompanyQuery();

  // Mutation for ticket assignment
  const [assignTicket, { isLoading: isAssigning }] = useAssignTicketMutation();

   // Handler for ticket assignment
   const handleTicketAssignment = async (event: React.FormEvent) => {
    event.preventDefault();
  
    if (!selectedAgentId) {
      toast.error("Please select an agent");
      return;
    }
  
    try {
      await assignTicket({
        ticketId: createdTicketId!,
        assignedAgentId: selectedAgentId
      }).unwrap();
  
      toast.success("Ticket assigned successfully");
      setShowAssignPopup(false);
      navigate(`/ticket/${createdTicketId}`);
    } catch (error) {
      console.error("Failed to assign ticket", error);
      toast.error("Failed to assign ticket. Please try again.");
    }
  };

  return (
    <MainLayout>
      <div className="create-ticket--content">
        <ContentHeader>Create Ticket</ContentHeader>

        <div className="create-ticket--form">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="ticket_title">Ticket Title</label>
              <input 
                type="text" 
                id="ticket_title"
                name="ticket_title"
                value={ticketTitle}
                onChange={(e) => setTicketTitle(e.target.value)}
                required 
              />
            </div>

            <div className="form-group">
              <label htmlFor="ticket_description">Ticket Description</label>
              <textarea 
                id="ticket_description"
                name="ticket_description"
                value={ticketDescription}
                onChange={(e) => setTicketDescription(e.target.value)}
                required
              />
            </div>

            {/* TICKET TYPE */}
            <div className="form-group">
              <label htmlFor="ticket_type_id">Ticket Type</label>
              <select 
                id="ticket_type_id"
                name="ticket_type_id"
                value={ticketTypeId?.toString() || ''}
                onChange={(e) => setTicketTypeId(e.target.value ? parseInt(e.target.value) : undefined)}
                required
                disabled={isTicketTypesLoading}
              >
                <option value="">Select Ticket Type</option>
                {ticketTypes?.map(ticket_type => (
                  <option 
                    key={ticket_type.ticket_type_id} 
                    value={ticket_type.ticket_type_id}
                  >
                    {ticket_type.ticket_type_name}
                  </option>
                ))}
              </select>
              {isTicketTypesError && <p className="error">Error loading ticket types</p>}
            </div>

            {/* CATEGORY TYPE */}
            <div className="form-group">
              <label htmlFor="category_type_id">Category Type</label>
              <select 
                id="category_type_id"
                name="category_type_id"
                value={selectedCategoryTypeId?.toString() || ''}
                onChange={handleCategoryTypeChange}
                required
                disabled={isCategoryTypesLoading}
              >
                <option value="">Select Category Type</option>
                {categoryTypes?.map(cat => (
                  <option 
                    key={cat.category_type_id} 
                    value={cat.category_type_id}
                  >
                    {cat.category_type_name}
                  </option>
                ))}
              </select>
              {isCategoryTypesError && <p className="error">Error loading category types</p>}
            </div>

            {/* CATEGORY */}
            <div className="form-group">
              <label htmlFor="category_id">Category</label>
              <select 
                id="category_id"
                name="category_id"
                value={selectedCategoryId?.toString() || ''}
                onChange={handleCategoryChange}
                required
                disabled={!selectedCategoryTypeId || isCategoriesLoading}
              >
                <option value="">
                  {!selectedCategoryTypeId 
                    ? "Select Category Type First" 
                    : isCategoriesLoading 
                    ? "Loading Categories..." 
                    : "Select Category"}
                </option>
                {categories?.map(cat => (
                  <option 
                    key={cat.category_id} 
                    value={cat.category_id}
                  >
                    {cat.category_name}
                  </option>
                ))}
              </select>
              {isCategoriesError && <p className="error">Error loading categories</p>}
            </div>

            {/* OPTIONAL SUBCATEGORY */}
            <div className="form-group">
              <label htmlFor="subcategory_id">Subcategory (Optional)</label>
              <select 
                id="subcategory_id"
                name="subcategory_id"
                value={selectedSubcategoryId?.toString() || ''}
                onChange={(event) => setSelectedSubcategoryId(event.target.value ? parseInt(event.target.value) : undefined)}
                disabled={!selectedCategoryId || isSubcategoriesLoading}
              >
                <option value="">
                  {!selectedCategoryId 
                    ? "Select Category First" 
                    : isSubcategoriesLoading 
                    ? "Loading Subcategories..." 
                    : "Select Subcategory (Optional)"}
                </option>
                {subcategories?.map(sub => (
                  <option 
                    key={sub.subcategory_id} 
                    value={sub.subcategory_id}
                  >
                    {sub.subcategory_name}
                  </option>
                ))}
              </select>
              {isSubcategoriesError && <p className="error">Error loading subcategories</p>}
            </div>

            {/* CLIENT */}
            <div className="form-group">
              <label htmlFor="client_id">Client</label>
              <select 
                id="client_id"
                name="client_id"
                value={clientId?.toString() || ''}
                onChange={(e) => setClientId(e.target.value ? parseInt(e.target.value) : undefined)}
                required
                disabled={isClientsLoading}
              >
                <option value="">Select Client</option>
                {clients?.map(client => (
                  <option 
                    key={client.client_id} 
                    value={client.client_id}
                  >
                    {client.client_username}
                  </option>
                ))}
              </select>
              {isClientsError && <p className="error">Error loading clients</p>}
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isCreatingTicket}
              >
                {isCreatingTicket ? "Creating Ticket..." : "Create Ticket"}
              </button>
              <button 
                type="reset" 
                className="reset-btn"
                onClick={() => {
                  setTicketTitle("");
                  setTicketDescription("");
                  setTicketTypeId(undefined);
                  setClientId(undefined);
                  setSelectedCategoryTypeId(undefined);
                  setSelectedCategoryId(undefined);
                  setSelectedSubcategoryId(undefined);
                }}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
         {/* Ticket Assignment Popup */}
         {showAssignPopup && (
          <div className="assign-ticket-popup-overlay">
            <div className="assign-ticket-popup">
              <h2 className="assign-ticket-popup-title">Assign Ticket</h2>
              <form onSubmit={handleTicketAssignment}>
                <div className="form-group">
                  <label htmlFor="agent_assignment">Select Agent</label>
                  <select 
                    id="agent_assignment"
                    value={selectedAgentId?.toString() || ''}
                    onChange={(e) => setSelectedAgentId(e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={isAgentsLoading}
                    required
                  >
                    <option value="">
                      {isAgentsLoading 
                        ? "Loading agents..." 
                        : "Select an agent"}
                    </option>
                    {agents?.map(agent => (
                      <option 
                        key={agent.agent_id} 
                        value={agent.agent_id}
                      >
                        {agent.agent_username}
                      </option>
                    ))}
                  </select>
                  {isAgentsError && (
                    <p className="error">Error loading agents</p>
                  )}
                </div>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="reset-btn"
                    onClick={() => setShowAssignPopup(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isAssigning || isAgentsLoading}
                  >
                    {isAssigning ? "Assigning..." : "Assign Ticket"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default CreateTicket;